// #2713: a lazy screen chunk that fails to load after a deploy (the old hashed file is gone) used to throw into the module boundary and blank the whole screen with a
// "New Version Available" card. Now: retry the import once; if it still fails, resolve with a tiny inline notice for THAT section only, and raise the slim "Charlie was updated"
// banner. The rest of the screen keeps working with its data and filters; nothing reloads until he taps Reload.
import React from 'react';

const CHUNK_ERR = [/Loading (CSS )?chunk [\w-]+ failed/i, /ChunkLoadError/i, /error loading dynamically imported module/i, /Failed to fetch dynamically imported module/i];
export const isChunkError = (e) => !!e && (String(e.name || '') === 'ChunkLoadError' || CHUNK_ERR.some((rx) => rx.test(String(e.message || ''))));

// ---- the tiny store the slim banner listens to ----
let stale = false;
const listeners = new Set();
export const chunkFailed = () => stale;
export function onChunkFailed(cb) { listeners.add(cb); if (stale) cb(); return () => listeners.delete(cb); }
export function markChunkFailed() { if (!stale) { stale = true; listeners.forEach((cb) => cb()); } }
export const resetChunkState = () => { stale = false; listeners.clear(); };

export function StaleSection() {
  return (
    <div role="status" data-testid="stale-section" style={{ fontSize: 13, color: 'var(--gov-slate-600)', border: '1px solid var(--gov-slate-200)', borderRadius: 8, padding: '10px 12px' }}>
      This section could not load because Charlie was updated. Use Reload in the bar at the top; the rest of this screen still works.
    </div>
  );
}

// factory: () => import('./X'). Retries once after a short wait; a second failure becomes the inline notice, never a thrown error.
export function withChunkRetry(factory, { wait = 800, sleep = (ms) => new Promise((r) => setTimeout(r, ms)) } = {}) {
  return () => factory().catch((err) => {
    if (!isChunkError(err)) throw err;
    return sleep(wait).then(() => factory()).catch((err2) => {
      if (!isChunkError(err2)) throw err2;
      markChunkFailed();
      return { default: StaleSection };
    });
  });
}

let installed = false;
// React.lazy is replaced once, before any screen module is evaluated (imported first in index.js), so every lazy() in the app gets the retry.
export function installLazyRetry(R = React) {
  if (installed || !R || typeof R.lazy !== 'function') return;
  installed = true;
  const original = R.lazy;
  R.lazy = (factory) => original(withChunkRetry(factory));
}
